import React, { useState } from 'react';

import {
  PageWrapper,
  StretchPanel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableCell,
  TableActions,
  useModal,
  useNotification,
  Flex,
  Button,
  Pagination,
  usePaginationReducer,
  OrderableTableHeadCell,
  useOrderableTableReducer,
  Modal,
  TextInput,
  Label,
  Explainer
} from '@sopranodesign/pulse-components';
import { useQueryClient, useQuery, useMutation } from '@sopranodesign/pulse-hooks';

import {
  deleteTrustedSender as deleteTrustedSenderApi,
  createTrustedSender as createTrustedSenderApi
} from '../api/index';

const TrustedSenders = () => {
  const order = useOrderableTableReducer({ id: 'trusted-senders' });
  const pagination = usePaginationReducer({ id: 'trusted-senders' });

  const { showModal, hideModal } = useModal();
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [fromAddress, setFromAddress] = useState('');

  const query = useQuery({
    queryKey: ['owner/trusted-senders', { ...pagination.params, ...order.params }]
  });

  const { mutateAsync: deleteTrustedSender } = useMutation({
    mutationFn: deleteTrustedSenderApi,
    onSuccess: () => {
      queryClient.invalidateQueries('owner/trusted-senders');
      showSuccess(`Trusted sender was successfully removed.`);
    }
  });

  const { mutateAsync: createTrustedSender } = useMutation({
    mutationFn: createTrustedSenderApi,
    onSuccess: () => {
      queryClient.invalidateQueries('owner/trusted-senders');
      showSuccess(`Trusted sender was successfully created.`);
      setShowCreateModal(false);
      setFromAddress('');
    }
  });

  const handleDelete = ({ id, from_address }) => {
    showModal({
      header: 'Remove Trusted Sender',
      body: `Are you sure you want to remove ${from_address} from the trusted senders list?`,
      handleConfirm: async () => {
        try {
          await deleteTrustedSender(id);
          hideModal();
        } catch (error) {
          hideModal();

          // if it's a conflict error from backend
          if (error.response?.status === 409) {
            const clientNames = error.response.data?.client_names || [];
            const message = error.response.data?.message || 'Domain is in use.';

            showError(`${message} Used by client(s): ${clientNames.join(', ')}`);
          } else {
            showError('An unexpected error occurred while deleting the trusted sender.');
          }
        }
      }
    });
  };

  const isValidDomain = (domain) =>
    /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain.trim());

  const handleCreate = async () => {
    if (!isValidDomain(fromAddress)) { return };
    await createTrustedSender({ from_address: fromAddress });
  };

  return (
    <PageWrapper
      header="Trusted Senders"
      subheader="Manage trusted email addresses"
      actions={
        <Flex xSpace>
          <Button icon="plus" variant="primary" onClick={() => setShowCreateModal(true)}>
            New Trusted Sender
          </Button>
        </Flex>
      }>
      <StretchPanel clip="true">
        <Table loading={query.isLoading} loadingColumns={2}>
          <TableHead>
            <TableRow>
              <OrderableTableHeadCell
                row={{ label: 'From Address', sort: 'from_address' }}
                {...order.props}
              />
              <TableHeadCell />
            </TableRow>
          </TableHead>
          {query.isSuccess && (
            <TableBody>
              {query.data.data.map((sender, key) => (
                <TableRow key={key}>
                  <TableCell>{sender.from_address}</TableCell>
                  <TableActions>
                    <Button
                      colour="danger"
                      variant="secondary"
                      icon="minus"
                      onClick={() => handleDelete(sender)}>
                      Remove
                    </Button>
                  </TableActions>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {query.showPagination && (
          <Pagination pagination={query.data.meta} {...pagination.props} />
        )}
      </StretchPanel>

      {showCreateModal && (
        <Modal
          header="New Trusted Sender"
          handleCancel={() => setShowCreateModal(false)}
          handleConfirm={handleCreate}
          confirmText="Create"
          cancelText="Cancel"
          disableConfirmButton={!isValidDomain(fromAddress)}
          body={
            <Flex col gap="sm">
              <Explainer>
                Please contact your account manager to initiate trusted sender activation.
              </Explainer>
              <Flex fat col>
                <Label htmlFor="trustedSender">Trusted Sender</Label>
                <TextInput
                  id="trustedsender"
                  value={fromAddress}
                  placeholder="example.com"
                  onChange={(e) => setFromAddress(e.target.value)}
                  required
                />
              </Flex>
            </Flex>
          }
        />
      )}
    </PageWrapper>
  );
};

export default TrustedSenders;